import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Determine API Base URL with smart fallback (local -> localhost:4000, prod -> api.tekkid.com)
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '');
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:4000';
  }
  return 'https://api.tekkid.com';
};

const API_BASE_URL = getApiBaseUrl();

console.log('🔧 API Configuration:', {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  API_BASE_URL,
  environment: import.meta.env.PROD ? 'production' : 'development'
});

// Create axios instance with proper base URL
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: import.meta.env.PROD ? 30000 : 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Disable credentials for CORS issues
});

// Add request interceptor to include JWT token
apiClient.interceptors.request.use((config) => {
  const storedAuth = localStorage.getItem('admin-auth');
  if (storedAuth) {
    try {
      const authData = JSON.parse(storedAuth);
      if (authData.token) {
        config.headers.Authorization = `Bearer ${authData.token}`;
      }
    } catch (error) {
      console.warn('Failed to parse stored auth data:', error);
    }
  }
  return config;
});

// Helper function to create form data from object
const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else if (value instanceof File) {
        formData.append(key, value);
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    }
  });
  return formData;
};

// Games API endpoints
export const gamesAPI = {
  getAll: () => apiClient.get('/games'),
  getAllForAdmin: () => apiClient.get('/games/admin'),
  getGameById: (id: string) => apiClient.get(`/games/${id}`),
  create: (gameData: any) => {
    const data = { ...gameData };
    if (data.image) {
      data.iconUrl = data.image;
      delete data.image;
    }
    return apiClient.post('/games', data);
  },
  createBulk: (payload: { games: any[]; defaultStatus?: string; skipExisting?: boolean }) => {
    return apiClient.post('/games/bulk', payload);
  },
  update: (id: string, gameData: any) => {
    const data = { ...gameData };
    if (data.image) {
      data.iconUrl = data.image;
      delete data.image;
    }
    return apiClient.put(`/games/${id}`, data);
  },
  delete: (id: string) => apiClient.delete(`/games/${id}`),
  softDelete: (id: string) => apiClient.patch(`/games/${id}/soft-delete`),
  restore: (id: string) => apiClient.patch(`/games/${id}/restore`),
  publish: (id: string) => apiClient.patch(`/games/${id}/publish`),
  unpublish: (id: string) => apiClient.patch(`/games/${id}/unpublish`),

  // Import SQL data (Deprecated - kept for backward compatibility)
  importSql: () => apiClient.post('/games/import-sql'),

  // Import from GameMonetize (Deprecated - use adminAPI.importFromGameMonetize instead)
  // Kept for backward compatibility during migration
  importFromGameMonetize: (options: {
    name?: string;
    category?: string;
    company?: string;
    platform?: string;
    links?: number;
    maxGames?: number;
    maxPages?: number;
  }) => apiClient.post('/admin/import/gamemonetize', options),

  // Upload a game ZIP file
  uploadGame: async (formData: FormData): Promise<any> => {
    try {
      // Get the auth data from localStorage
      const storedAuth = localStorage.getItem('admin-auth');
      if (!storedAuth) {
        throw new Error('No authentication data found. Please log in again.');
      }
      
      const authData = JSON.parse(storedAuth);
      const token = authData.token;
      const user = authData.user;
      const userRoles = user?.roles || [];
      const userId = user?.id;
      const username = user?.username;

      console.log('Current user info:', { 
        userId, 
        username, 
        roles: userRoles,
        hasToken: !!token,
        user
      });
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      if (!userRoles.includes('ADMIN') && !userRoles.includes('CREATOR')) {
        throw new Error('Insufficient permissions. You need CREATOR or ADMIN role to upload games.');
      }

      // Log form data (excluding file content for security)
      const formDataObj: Record<string, unknown> = {};
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          formDataObj[key] = { name: value.name, size: value.size };
        } else {
          formDataObj[key] = value;
        }
      }
      console.log('Uploading game with form data:', formDataObj);
      
      // Log the upload endpoint
      console.log('Uploading to: /game-upload/upload (with apiClient base URL)');
      
      const response = await apiClient.post('/game-upload/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
          'X-User-Id': userId || '',
          'X-User-Roles': userRoles.join(',')
        },
        timeout: 300000, // 5 minute timeout for large uploads
        withCredentials: true,
        maxBodyLength: Infinity,
        maxContentLength: Infinity
      });
      
      console.log('Upload successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Upload failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  }
};

// Categories API endpoints
export const categoriesAPI = {
  getAll: (isAdmin: boolean = false) => apiClient.get('/categories', { params: { admin: isAdmin } }),
  getById: (id: string) => apiClient.get(`/categories/${id}`),
  create: (categoryData: any) => apiClient.post('/categories', categoryData),
  update: (id: string, categoryData: any) => apiClient.put(`/categories/${id}`, categoryData),
  delete: (id: string) => apiClient.delete(`/categories/${id}`)
};

// Tags API endpoints
export const tagsAPI = {
  getAll: () => apiClient.get('/tags'),
  getById: (id: string) => apiClient.get(`/tags/${id}`),
  create: (tagData: any) => apiClient.post('/tags', tagData),
  update: (id: string, tagData: any) => apiClient.put(`/tags/${id}`, tagData),
  delete: (id: string) => apiClient.delete(`/tags/${id}`)
};

// Setup API endpoints
export const setupAPI = {
  getDatabaseStats: () => apiClient.get('/setup/database-stats'),
  createAdminUser: (userData: { username: string; email: string; password: string; firstName: string; lastName: string }) =>
    apiClient.post('/setup/create-admin', userData),
  checkAdminExists: () => apiClient.get('/setup/check-admin'),
  seedDatabase: () => apiClient.post('/setup/seed-games'),
};

// Auth API endpoints
export const authAPI = {
  login: (credentials: { email?: string; username?: string; password: string }) =>
    apiClient.post('/auth/login', credentials),
  logout: () => apiClient.post('/auth/logout'),
  register: (userData: any) => apiClient.post('/auth/register', userData),
  getCurrentUser: () => apiClient.get('/auth/me'),
  refreshToken: () => apiClient.post('/auth/refresh'),
  forgotPassword: (email: string) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    apiClient.post('/auth/reset-password', { token, newPassword }),
  verify: () => apiClient.get('/auth/verify'),
  getAllUsers: () => apiClient.get('/auth/users'),
  deleteUser: (id: string) => apiClient.delete(`/auth/users/${id}`),
  updateUser: (id: string, userData: any) => apiClient.put(`/auth/users/${id}`, userData),
  createUser: (userData: any) => apiClient.post('/auth/users', userData),
  updateUserStatus: (id: string, isActive: boolean) => apiClient.patch(`/auth/users/${id}/status`, { isActive }),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiClient.patch('/auth/change-password', { currentPassword, newPassword }),
  resetUserPassword: (id: string, newPassword: string) =>
    apiClient.patch(`/auth/users/${id}/reset-password`, { newPassword }),
};

export default apiClient;
